import getTruthyKeys from "./getTruthyKeys";

const parseSelectedPlacements = (
    selected,
    options,
    hasSeveralRightsholders,
) => {
    let selectedPlacementSet = new Set();
    if (
        selected &&
        typeof selected === 'object' &&
        Object.keys(selected).length > 0
    ) {
        /*
            Creating a set based on distinct rightsholder placement combinations
            selected.placements should come in format of {rightsholderId}-${placementIds}
            where placementIds is separated by commas.
         */
        selectedPlacementSet = new Set(
            Object.keys(selected)
                .filter(k => selected[k])
                .flatMap(rpids => {
                    const [rid, pids] = rpids.split('-');
                    const placements = pids.split(',');
                    return placements.map(p => `${rid}-${p}`);
                }),
        );
    }
    const isPlacementSelected = (rightsholderId, placementGroup) => {
        const pids = placementGroup.toString().split(',');
        return pids.some(pid =>
            selectedPlacementSet.has(`${rightsholderId}-${pid}`),
        );
    };
    if (!hasSeveralRightsholders) {
        // If there is no nested, means that value has format: ${rid}-${pid}
        return options
            .filter(plc => {
                const [rid, pid] = plc.value.split('-');
                return isPlacementSelected(rid, pid);
            })
            .reduce((acc, option) => {
                acc[option.value] = true;
                return acc;
            }, {});
    } else {
        return options
            .flatMap(plc => {
                const rid = plc.value;
                return plc.nested
                    .filter(placementGroup => {
                        //plc.nested could have placements separated by commas or in separate rightsholder placements.
                        return isPlacementSelected(rid, placementGroup.value);
                    })
                    .map(p => `${rid}-${p.value}`);
            })
            .reduce((acc, value) => {
                acc[value] = true;
                return acc;
            }, {});
    }
};

export const parseSelectedRightsholderPlacements = (value, placements) => {
    if (!value) return [];

    const normalized: string[] = Array.isArray(value)
        ? value
        : value && typeof value === 'object'
            ? (Object.keys(value)?.length ? getTruthyKeys(value) : [])
            : typeof value === 'string'
                ? (value.length ? [value] : [])
                : [];

    if (!normalized.length) return [];

    const rightsholderPlacementsKeys = normalized.filter(v =>
        typeof v === 'string' && v.startsWith('rh_'),
    );

    const rightsholderPlacements = rightsholderPlacementsKeys.reduce((acc, v) => {
        const rhId = v.split('_')[1];

        if (placements && !Array.isArray(placements)) {
            // Legacy: placements is an object map like {"<rid>-<pid>": true}
            const rhPlacements = Object.keys(placements).filter(plcId =>
                plcId.startsWith(`${rhId}-`),
            );
            acc.push(...rhPlacements);
            return acc;
        }

        const rhOption = Array.isArray(placements)
            ? placements.find(o => `${o.value}` === `${rhId}`)
            : null;

        if (!rhOption || !Array.isArray(rhOption.nested)) return acc;

        rhOption.nested.forEach(n => {
            const pid = `${n.value}`;
            if (pid.includes(',')) {
                pid.split(',').forEach(p => acc.push(`${rhId}-${p}`));
            } else {
                acc.push(`${rhId}-${pid}`);
            }
        });

        return acc;
    }, []);
    const otherRPs = normalized
        .filter(v => typeof v === 'string' && !v.startsWith('rh_'))
        .reduce((acc, v) => {
            const [rhId, plcId] = v.split('-');
            if (plcId.includes(',')) {
                const pls = plcId.split(',');
                acc.push(...pls.map(p => `${rhId}-${p}`));
            } else {
                acc.push(v);
            }
            return acc;
        }, []);

    // handle grouped placements

    return [...rightsholderPlacements, ...otherRPs];
};

export default parseSelectedPlacements;
